<script setup>
import { computed, ref } from 'vue'
import UiModal from '@/components/ui/UiModal.vue'
import LinesCompare from '@/components/catalogue/versions/LinesCompare.vue'
import { compareDocs } from '@/components/catalogue/versions/compare.js'
import { brief, settled } from '@/lib/textChange.js'

/**
 * A formatted text changed (a note, a description), as a person reads it — not the stored JSON
 * (D-348): in the history briefly, the timeline kept short — the first paragraphs that changed,
 * only the words about each change, at most three lines each; the whole text with its changes a
 * click away.
 */
const props = defineProps({
  from: { type: Object, default: null },
  to: { type: Object, default: null },
  label: { type: String, default: '' },
  // Shown blurred in privacy mode (D-260).
  sensitive: Boolean,
})
const entries = computed(() => settled(compareDocs(props.from, props.to)))
const short = computed(() => brief(entries.value))
const open = ref(false)
</script>

<template>
  <div class="min-w-0 flex-1" data-testid="text-change">
    <p v-if="!short.lines.length" class="text-faint">{{ $t('timeline.formattingOnly') }}</p>
    <ul v-else v-private="sensitive" class="lines-compare space-y-0.5" data-testid="text-change-brief">
      <li v-for="(line, i) in short.lines" :key="i" :data-state="line.state" :class="['doc-words line-clamp-3 rounded px-1.5 py-0.5', line.state === 'added' ? 'bg-emerald-50 dark:bg-emerald-900/30' : line.state === 'removed' ? 'bg-red-50 dark:bg-red-900/30' : 'bg-amber-50 dark:bg-amber-900/20']"><template v-for="(p, j) in line.parts" :key="j"><span v-if="p.type === 'same'">{{ p.text }}</span><span v-else-if="p.type === 'del'" class="removed">{{ p.text }}</span><span v-else class="changed">{{ p.text }}</span></template></li>
    </ul>
    <p class="mt-0.5 flex flex-wrap items-center gap-2">
      <span v-if="short.more" class="text-faint">{{ $t('timeline.moreChanges', { n: short.more }) }}</span>
      <button type="button" class="link" data-testid="text-change-open" @click="open = true">{{ $t('timeline.showDiff') }}</button>
    </p>
    <UiModal :open="open" :title="$t('timeline.diffTitle', { field: label })" size="lg" @close="open = false">
      <LinesCompare v-private="sensitive" :entries="entries" data-testid="text-change-full" />
    </UiModal>
  </div>
</template>
